import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { reportApi } from '../../api/report.api';
import { departmentApi } from '../../api/department.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner, Select, Input } from '../../components/ui/index';

const DEFAULT_AMIN_HOST = '192.168.19.109';
const DEFAULT_AMIN_PORT = '8000';

export default function DailyReportRoutingPage() {
  const { addToast } = useUI();
  const [departments, setDepartments] = useState([]);
  const [routing, setRouting] = useState({});
  const [financeDeptId, setFinanceDeptId] = useState('');
  const [aminHost, setAminHost] = useState('');
  const [aminPort, setAminPort] = useState('');
  const [bridgeConfigured, setBridgeConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBridge, setSavingBridge] = useState(false);
  const [testingBridge, setTestingBridge] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [deptRes, routeRes, bridgeRes] = await Promise.all([
        departmentApi.getDepartmentListView(),
        reportApi.getDailyReportRouting(),
        reportApi.getAccountingBridgeConfig(),
      ]);
      setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
      const r = routeRes.data && typeof routeRes.data === 'object' ? routeRes.data : {};
      setRouting(r);
      setFinanceDeptId(r.finance_invoice?.department_id ? String(r.finance_invoice.department_id) : '');
      const b = bridgeRes.data && typeof bridgeRes.data === 'object' ? bridgeRes.data : {};
      setAminHost(b.amin_host || DEFAULT_AMIN_HOST);
      setAminPort(b.amin_port != null ? String(b.amin_port) : DEFAULT_AMIN_PORT);
      setBridgeConfigured(!!b.configured);
    } catch {
      addToast('خطا در بارگذاری تنظیمات مسیریابی', 'error');
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const deptOptions = [
    { value: '', label: '— انتخاب دپارتمان مالی —' },
    ...departments.map((d) => ({ value: String(d.id), label: d.name })),
  ];

  const save = async () => {
    if (!financeDeptId) {
      addToast('دپارتمان مالی را انتخاب کنید', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await reportApi.saveDailyReportRouting({
        finance_invoice_department_id: Number(financeDeptId),
      });
      const updated = res.data && typeof res.data === 'object' ? res.data : {};
      setRouting(updated);
      addToast('دپارتمان مالی ذخیره شد — همه فاکتورها و هزینه‌ها به این دپارتمان می‌روند', 'success');
    } catch {
      addToast('خطا در ذخیره تنظیمات', 'error');
    }
    setSaving(false);
  };

  const saveBridge = async () => {
    if (!aminHost.trim() || !aminPort.trim()) {
      addToast('IP و پورت سرور حسابداری را وارد کنید', 'error');
      return;
    }
    setSavingBridge(true);
    try {
      const res = await reportApi.saveAccountingBridgeConfig({
        amin_host: aminHost.trim(),
        amin_port: Number(aminPort),
      });
      setBridgeConfigured(!!res.data?.configured);
      addToast('تنظیمات سرور حسابداری واسط (امین) ذخیره شد', 'success');
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در ذخیره سرور حسابداری', 'error');
    }
    setSavingBridge(false);
  };

  const testBridge = async () => {
    setTestingBridge(true);
    try {
      const res = await reportApi.testAminConnection();
      if (res.data?.ok) {
        addToast('اتصال به سرور امین برقرار است', 'success');
      } else {
        addToast(res.data?.error || 'اتصال ناموفق بود', 'error');
      }
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : (err.response?.data?.error || 'اتصال ناموفق بود'), 'error');
    }
    setTestingBridge(false);
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="مسیریابی گزارش روزانه"
        subtitle="تعیین دپارتمان مالی و سرور حسابداری واسط (امین)"
        action={
          <Link to="/dispatcher" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}>
            ← صف دیسپچر
          </Link>
        }
      />

      <div style={{
        background: '#EDF3FC', border: '1px solid #B7C9EC', borderRadius: 12,
        padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#1e40af', lineHeight: 1.8,
      }}>
        <strong>همه</strong> فاکتورها و هزینه‌های گزارش روزانه فقط به <strong>دپارتمان مالی</strong> انتخاب‌شده می‌روند.
        مدیر مالی هنگام قیمت‌گذاری، <strong>کد کالا</strong> را از سرور حسابداری واسط (امین) دریافت می‌کند.
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>
      ) : (
        <div className="pj-split-grid">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{
              background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.12)', padding: 20,
            }}
            >
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>دپارتمان مالی</h3>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 14px', lineHeight: 1.7 }}>
                وقتی کارمند در گزارش روزانه بخش «نیاز به هزینه/فاکتور» را ثبت کند، ردیف در لیست فاکتورهای این دپارتمان ایجاد می‌شود.
              </p>
              <Select
                label="دپارتمان مالی *"
                value={financeDeptId}
                onChange={(e) => setFinanceDeptId(e.target.value)}
                options={deptOptions}
              />
              {routing.finance_invoice?.department_name && (
                <p style={{ fontSize: 11, color: '#16a34a', marginTop: 10, marginBottom: 0 }}>
                  فعلی: {routing.finance_invoice.department_name}
                </p>
              )}
            </div>

            <div style={{
              background: '#fff', borderRadius: 14, border: '1px solid rgba(16,185,129,0.15)', padding: 20,
            }}
            >
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
                سرور حسابداری واسط (امین)
              </h3>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 14px', lineHeight: 1.7 }}>
                IP و پورت سرویس FastAPI امین — CRM مستقیماً به امین وصل نمی‌شود؛ فقط از طریق بک‌اند Django.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <Input
                  label="IP سرور *"
                  value={aminHost}
                  onChange={(e) => setAminHost(e.target.value)}
                  placeholder={DEFAULT_AMIN_HOST}
                />
                <Input
                  label="پورت *"
                  value={aminPort}
                  onChange={(e) => setAminPort(e.target.value)}
                  placeholder={DEFAULT_AMIN_PORT}
                  type="number"
                  min="1"
                  max="65535"
                />
              </div>
              {bridgeConfigured && (
                <p style={{ fontSize: 11, color: '#16a34a', margin: '0 0 12px' }}>
                  پیکربندی فعال: {aminHost}:{aminPort}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn onClick={saveBridge} loading={savingBridge}>ذخیره سرور</Btn>
                <Btn variant="secondary" onClick={testBridge} loading={testingBridge}>تست اتصال</Btn>
              </div>
            </div>
          </div>

          <div style={{
            background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.12)',
            padding: 20, position: 'sticky', top: 20,
          }}
          >
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 12px' }}>خلاصه</h3>
            <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.9, margin: '0 0 8px' }}>
              مقصد فاکتور:
              {' '}
              <strong>{routing.finance_invoice?.department_name || '— تعیین نشده —'}</strong>
            </p>
            <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.9, margin: '0 0 16px' }}>
              سرور امین:
              {' '}
              <strong>{bridgeConfigured ? `${aminHost}:${aminPort}` : '— پیکربندی نشده —'}</strong>
            </p>
            <Btn onClick={save} loading={saving} style={{ width: '100%', justifyContent: 'center' }}>
              ذخیره دپارتمان مالی
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}
